import React from 'react'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { numberPrecision } from '../../../../helpers/UtilityHelper'

const ZoneWiseAttemptAgeingKeys = [
    'atZeroDay', 'atOneDay', 'atTwoDay', 'atThreeDay',
    'atFourDay', 'atFiveDay', 'atSixDay', 'atSevenAndMoreDay'
]

function getDayLabels(prefix) {
    return {
        atZeroDay: `${prefix}-0`,
        atOneDay: `${prefix}-1`,
        atTwoDay: `${prefix}-2`,
        atThreeDay: `${prefix}-3`,
        atFourDay: `${prefix}-4`,
        atFiveDay: `${prefix}-5`,
        atSixDay: `${prefix}-6`,
        atSevenAndMoreDay: `${prefix}-7+`,
    }
}

const HEADER_BG = '#FFEB3B'

function getCellBg(val) {
    if (val >= 30) return '#c8e6c9'
    if (val >= 10) return '#fff9c4'
    if (val >= 1) return '#ffe0b2'
    return 'inherit'
}

const headerCellSx = {
    backgroundColor: HEADER_BG,
    fontWeight: 'bold',
    fontSize: '0.75rem',
    border: '1px solid #ccc',
    whiteSpace: 'nowrap',
    padding: '6px 10px',
}

const stickySx = { position: 'sticky', left: 0, zIndex: 2, backgroundColor: HEADER_BG }

export default function ZoneDayWiseAttemptTable({ data = [], labelPrefix = 'P2A' }) {
    const DAY_LABELS = React.useMemo(() => getDayLabels(labelPrefix), [labelPrefix])
    const zoneMap = React.useMemo(() => Object.fromEntries(data.map((d) => [d.name, d])), [data])

    const zones = React.useMemo(() => {
        const order = ['LOCAL', 'METROS', 'REGIONAL', 'ROI', 'SPECIAL']
        const present = new Set(data.map((d) => d.name))
        const ordered = order.filter((z) => present.has(z))
        data.forEach((d) => {
            if (!ordered.includes(d.name)) ordered.push(d.name)
        })
        return ordered
    }, [data])

    const zoneTotals = React.useMemo(() => {
        return Object.fromEntries(
            zones.map((z) => {
                const d = zoneMap[z]
                if (!d) return [z, 0]
                const total = ZoneWiseAttemptAgeingKeys.reduce((sum, key) => sum + (d[key] || 0), 0)
                return [z, total]
            })
        )
    }, [zones, zoneMap])

    const grandTotalShipments = React.useMemo(
        () => Object.values(zoneTotals).reduce((a, b) => a + b, 0),
        [zoneTotals]
    )

    const rows = React.useMemo(
        () =>
            ZoneWiseAttemptAgeingKeys.map((key) => {
                const row = { day: DAY_LABELS[key], key }
                let countForDay = 0
                zones.forEach((z) => {
                    const d = zoneMap[z]
                    const count = d ? d[key] ?? 0 : 0
                    row[z] = d ? d[`${key}Percentage`] ?? 0 : 0
                    row[`${z}_count`] = count
                    countForDay += count
                })
                row.grandTotalCount = countForDay
                row.grandTotal =
                    grandTotalShipments > 0
                        ? Number(numberPrecision((countForDay / grandTotalShipments) * 100))
                        : 0
                return row
            }),
        [zones, zoneMap, grandTotalShipments, DAY_LABELS]
    )

    return (
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ borderCollapse: 'collapse', minWidth: 500 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ ...headerCellSx, ...stickySx }}>Attempt Day</TableCell>
                        {zones.map((z) => (
                            <TableCell key={z} align='center' sx={headerCellSx}>
                                {z}
                            </TableCell>
                        ))}
                        <TableCell align='center' sx={headerCellSx}>
                            Grand Total
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.day} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                            <TableCell
                                sx={{
                                    fontWeight: 'bold',
                                    border: '1px solid #ccc',
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: '#fff',
                                    zIndex: 1,
                                    fontSize: '0.75rem',
                                    padding: '5px 10px',
                                }}
                            >
                                {row.day}
                            </TableCell>
                            {zones.map((z) => (
                                <TableCell
                                    key={z}
                                    align='center'
                                    sx={{
                                        border: '1px solid #ccc',
                                        backgroundColor: getCellBg(row[z]),
                                        fontSize: '0.75rem',
                                        padding: '5px 10px',
                                    }}
                                >
                                    {row[`${z}_count`]} ({numberPrecision(row[z], 2)}%)
                                </TableCell>
                            ))}
                            <TableCell
                                align='center'
                                sx={{
                                    border: '1px solid #ccc',
                                    fontWeight: 'bold',
                                    backgroundColor: getCellBg(row.grandTotal),
                                    fontSize: '0.75rem',
                                    padding: '5px 10px',
                                }}
                            >
                                {row.grandTotalCount} ({numberPrecision(row.grandTotal, 2)}%)
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: '#212121' }}>
                        <TableCell
                            sx={{
                                color: '#fff',
                                fontWeight: 'bold',
                                border: '1px solid #555',
                                position: 'sticky',
                                left: 0,
                                backgroundColor: '#212121',
                                zIndex: 1,
                                fontSize: '0.75rem',
                                padding: '5px 10px',
                            }}
                        >
                            Grand Total
                        </TableCell>
                        {zones.map((z) => (
                            <TableCell
                                key={z}
                                align='center'
                                sx={{
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    border: '1px solid #555',
                                    fontSize: '0.75rem',
                                    padding: '5px 10px',
                                }}
                            >
                                {zoneTotals[z]} (100%)
                            </TableCell>
                        ))}
                        <TableCell
                            align='center'
                            sx={{
                                color: '#fff',
                                fontWeight: 'bold',
                                border: '1px solid #555',
                                fontSize: '0.75rem',
                                padding: '5px 10px',
                            }}
                        >
                            {grandTotalShipments} (100%)
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    )
}
